import { useEffect, useRef, useState } from 'react';
import type { CustomerPhotoWorkflowStatus } from '@tcvs/shared';
import type { CatalogDetailResponse, useCatalogApi } from './catalog-api';

type Catalog = Pick<
  ReturnType<typeof useCatalogApi>,
  'customerPhotoStatus' | 'publishCustomerPhotos'
>;
type Props = {
  card: CatalogDetailResponse | null;
  catalog: Catalog;
  canReview: boolean;
  operatorId?: string;
};
const messages: Record<CustomerPhotoWorkflowStatus['state'], string> = {
  NOT_APPROVED: 'Approve the current images before publishing customer photos.',
  READY: 'Approved photos are ready to publish.',
  PUBLISHED: 'Customer photos are published.',
  IN_PROGRESS: 'Publication is in progress. Refresh to check its status.',
  RETRYABLE: 'Publication can be retried when available.',
  BLOCKED: 'Customer photos cannot be published. Review the current card and images.',
};

function validStatus(value: CustomerPhotoWorkflowStatus, cardId: string): boolean {
  return (
    value !== null &&
    typeof value === 'object' &&
    value.cardId === cardId &&
    Object.hasOwn(messages, value.state) &&
    typeof value.canPublish === 'boolean' &&
    (value.revision === null || (Number.isSafeInteger(value.revision) && value.revision > 0)) &&
    (!['READY', 'PUBLISHED', 'RETRYABLE'].includes(value.state) || value.revision !== null) &&
    (!value.canPublish ||
      (value.revision !== null && ['READY', 'RETRYABLE'].includes(value.state))) &&
    (value.retryAfterSeconds === undefined ||
      (Number.isSafeInteger(value.retryAfterSeconds) && value.retryAfterSeconds >= 0))
  );
}

/** Changing source state or operator unmounts the prior request owner immediately. */
export function CustomerPhotoPanel({ card, catalog, canReview, operatorId }: Props) {
  if (!canReview || !card?.versionedImageReviewRequired) return null;
  const source = card as unknown as Record<string, unknown>;
  const context = JSON.stringify([
    card.cardId,
    operatorId,
    source.customerPhotoRevision,
    source.customerPhotoLifecycleId,
    card.imageQualityStatus,
    card.imageQualityReviewedAt,
    source.imageReplacedAt,
    card.pendingImageReplacement?.replacementId,
  ]);
  return <Publication key={context} cardId={card.cardId} catalog={catalog} />;
}

function Publication({ cardId, catalog }: { cardId: string; catalog: Catalog }) {
  const [status, setStatus] = useState<CustomerPhotoWorkflowStatus | null>(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unsupported, setUnsupported] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const owner = useRef({ epoch: 0, active: false, controller: new AbortController() });
  const waitTimer = useRef<ReturnType<typeof setTimeout>>();
  const requestTimer = useRef<ReturnType<typeof setTimeout>>();

  async function request(expectedRevision?: number) {
    if (owner.current.active) return;
    const epoch = ++owner.current.epoch;
    const controller = new AbortController();
    owner.current = { epoch, active: true, controller };
    const current = () => epoch === owner.current.epoch;
    setBusy(true);
    setError(null);
    setStatus(null);
    setUnsupported(false);
    setWaiting(false);
    const failure =
      expectedRevision === undefined
        ? 'Photo status is unavailable. Refresh to try again.'
        : 'Publication outcome is unconfirmed. Refresh status before trying again.';
    requestTimer.current = setTimeout(() => {
      if (!current()) return;
      owner.current.epoch++;
      owner.current.active = false;
      controller.abort();
      setBusy(false);
      setError(failure);
    }, 30000);
    try {
      const result =
        expectedRevision === undefined
          ? await catalog.customerPhotoStatus(cardId, controller.signal)
          : await catalog.publishCustomerPhotos(cardId, { expectedRevision }, controller.signal);
      if (!current()) return;
      if (!result.ok) {
        if (result.status === 404 && expectedRevision === undefined) {
          setUnsupported(true);
          return;
        }
        throw new Error();
      }
      if (
        !validStatus(result.data, cardId) ||
        (expectedRevision !== undefined && result.data.revision !== expectedRevision)
      )
        throw new Error();
      setStatus(result.data);
      const seconds = result.data.retryAfterSeconds ?? 0;
      setWaiting(seconds > 0);
      if (waitTimer.current) clearTimeout(waitTimer.current);
      if (seconds > 0)
        waitTimer.current = setTimeout(
          () => setWaiting(false),
          Math.min(seconds * 1000, 2147483647),
        );
    } catch {
      if (current()) setError(failure);
    } finally {
      if (current()) {
        if (requestTimer.current) clearTimeout(requestTimer.current);
        owner.current.active = false;
        setBusy(false);
      }
    }
  }

  useEffect(() => {
    void request();
    return () => {
      owner.current.epoch++;
      owner.current.active = false;
      owner.current.controller.abort();
      if (requestTimer.current) clearTimeout(requestTimer.current);
      if (waitTimer.current) clearTimeout(waitTimer.current);
    };
    // A new API client also invalidates any response from the prior session.
  }, [cardId, catalog]);

  if (unsupported) return null;
  return (
    <section aria-label="Customer photos" style={{ marginTop: 24 }}>
      <h3>Customer photos</h3>
      {busy ? <p role="status">Checking customer photos...</p> : null}
      {error ? <p role="alert">{error}</p> : null}
      {status ? <p role="status">{messages[status.state]}</p> : null}
      {waiting ? <p className="tcvs-muted">Please wait before checking or trying again.</p> : null}
      {status?.canPublish && status.revision !== null ? (
        <button
          type="button"
          disabled={busy || waiting}
          onClick={() => void request(status.revision!)}
        >
          {status.state === 'RETRYABLE' ? 'Retry publication' : 'Publish customer photos'}
        </button>
      ) : null}
      <button type="button" disabled={busy || waiting} onClick={() => void request()}>
        Refresh photo status
      </button>
    </section>
  );
}
